/**
 * Authenticated API Client
 *
 * Provides a centralized way to make authenticated API requests with:
 * - Automatic token injection from useAccessToken()
 * - Retry logic with exponential backoff (max 3 attempts)
 * - Loading state management
 * - User-friendly error handling
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAccessToken } from '../auth/hooks';
import { getAuthConfig } from '../config/auth';
import type { ApiError, ApiRequestState } from './types';
import { mapErrorToUserMessage } from './errors';
import { LOCAL_API_URL } from '../config/apiTargets';

/** Maximum number of retry attempts */
const MAX_RETRIES = 3;

/** Base delay for exponential backoff (ms) */
const BASE_DELAY = 1000;

/**
 * HTTP 502 — an upstream system api-edna depends on failed.
 *
 * The service returns this (never a 500) when Brandwatch, LexisNexis, Airflow, or
 * the Athena-backed reporting queries fail or time out, and the response body names
 * the system. Called out as a constant because it is the one 5xx this client treats
 * differently — see `shouldAutoRetry`.
 */
const HTTP_UPSTREAM_FAILURE = 502;

/**
 * Should the fetch layer transparently retry this failure?
 *
 * Deliberately NOT the same question as "may the user retry?" — see `userMayRetry`.
 * Conflating the two is what suppressed the Try Again button on reporting failures:
 * the flag was computed from the internal retry counter, so it was always false by
 * the time the error reached the UI.
 *
 * Transient-and-cheap failures are worth retrying silently. A 502 is neither:
 *
 * - It means a *named upstream* failed, not that this service hiccuped. A 1–2s
 *   backoff does not fix a provider outage or an unprovisioned query engine.
 * - The reporting tier's server-side budget is 60s, so three attempts cost roughly
 *   three minutes before the user sees anything. Retrying turned a 60s failure into
 *   a 3-minute one.
 *
 * So 502 fails fast and hands the decision to the user, who gets a button and the
 * service's own explanation of which system is down.
 */
function shouldAutoRetry(status: number): boolean {
  if (status === HTTP_UPSTREAM_FAILURE) return false;
  // Network errors (0) and other 5xx are plausibly transient.
  return status === 0 || status >= 500;
}

/**
 * May the *user* usefully retry this, i.e. should a Try Again button appear?
 *
 * Broader than `shouldAutoRetry`, and independent of how many internal attempts
 * have been made. A 502 belongs here even though it is not auto-retried: an
 * upstream that is down now may well be up in a minute, and offering the choice is
 * strictly better than making the user reload the page (FR-019).
 *
 * 4xx are excluded — a refused delete or an invalid role will be refused again.
 */
function userMayRetry(status: number): boolean {
  return status === 0 || status >= 500;
}

/**
 * Get the current API URL.
 *
 * Checks localStorage for dev mode API target override,
 * otherwise uses the configured API URL.
 */
function getApiUrl(): string {
  const config = getAuthConfig();

  // Check for dev mode API target override
  if (import.meta.env.VITE_ENVIRONMENT === 'dev') {
    try {
      const stored = localStorage.getItem('vibe-dev-mode-config');
      if (stored) {
        const devConfig = JSON.parse(stored);
        if (devConfig.apiTarget === 'local') {
          return LOCAL_API_URL;
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }

  return config.apiUrl;
}

/**
 * Sleep for a given duration.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Make an authenticated fetch request with retry logic.
 *
 * @param endpoint - API endpoint (relative to API base URL)
 * @param token - Access token for Authorization header
 * @param options - Additional fetch options
 * @param retryCount - Current retry attempt
 * @returns Response data or throws an error
 */
/**
 * Ensure endpoint has proper trailing slash to avoid 307 redirects.
 *
 * FastAPI routes like /clients redirect to /clients/ which can cause
 * issues with Authorization headers being dropped during redirect.
 *
 * The /internal/* (Edna) routers define exact paths (e.g.
 * /internal/pipelines/{id}/status, /internal/datasources/lexisnexis/validate)
 * WITHOUT trailing slashes. Appending one there causes the opposite problem —
 * a 307 redirect that drops CORS headers cross-origin and surfaces as a
 * network error — so those paths are left untouched.
 */
function normalizeEndpoint(endpoint: string): string {
  // Split path and query string
  const [path, query] = endpoint.split('?');

  // /internal/* routes are defined with exact paths; never add a slash.
  if (path.startsWith('/internal/')) {
    return endpoint;
  }

  // If path already ends with / or contains a specific resource ID, leave it
  if (path.endsWith('/') || /\/\d+$/.test(path)) {
    return endpoint;
  }

  // Add trailing slash to collection endpoints
  const normalizedPath = path + '/';
  return query ? `${normalizedPath}?${query}` : normalizedPath;
}

/**
 * Pull FastAPI's `detail` string out of an error response body.
 *
 * The backend often explains the actual cause there (e.g. the Athena-backed
 * monitoring query timing out), which is far more useful than what the status
 * code alone can convey. Returns null when the body is empty, isn't JSON, or
 * has no usable string detail — 422 validation errors, for instance, put a
 * list of objects in `detail` — so callers fall back to the generic message.
 */
async function readErrorDetail(response: Response): Promise<string | null> {
  try {
    const text = await response.text();
    if (!text) return null;
    const detail = (JSON.parse(text) as { detail?: unknown })?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    // FastAPI's 422 puts a LIST of per-field objects in `detail` rather than a
    // string, so the string check above misses exactly the errors that name what
    // the caller got wrong. Flatten them into one message instead of falling back
    // to the generic "Invalid request" (FR-018).
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => {
          const entry = item as { loc?: unknown[]; msg?: unknown };
          if (typeof entry?.msg !== 'string') return null;
          // `loc` is like ["body", "role"] — the last element is the field name.
          const field = Array.isArray(entry.loc)
            ? entry.loc.filter((p) => typeof p === 'string' && p !== 'body').pop()
            : undefined;
          return field ? `${String(field)}: ${entry.msg}` : entry.msg;
        })
        .filter((m): m is string => !!m);
      if (messages.length) return messages.join('; ');
    }
    return null;
  } catch {
    return null;
  }
}

async function authenticatedFetch<T>(
  endpoint: string,
  token: string | null,
  options: RequestInit = {},
  retryCount = 0
): Promise<T> {
  const apiUrl = getApiUrl();
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const url = `${apiUrl}${normalizedEndpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add Authorization header if token is available
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Merge with any provided headers
  const finalHeaders = options.headers
    ? { ...headers, ...Object.fromEntries(new Headers(options.headers).entries()) }
    : headers;

  try {
    const response = await fetch(url, {
      ...options,
      headers: finalHeaders,
    });

    // Handle auth errors - don't retry
    if (response.status === 401 || response.status === 403) {
      const error: ApiError = {
        status: response.status,
        message: mapErrorToUserMessage(response.status),
        isAuthError: true,
        canRetry: false,
      };
      throw error;
    }

    // Handle other errors. `canRetry` describes whether the USER may retry, and is
    // deliberately independent of `retryCount` — the auto-retry decision is made in
    // the catch block below via `shouldAutoRetry`. Folding the internal counter into
    // this flag meant it was always false by the time the UI saw it, which silently
    // suppressed the Try Again button on exactly the failures worth retrying.
    if (!response.ok) {
      const error: ApiError = {
        status: response.status,
        message:
          (await readErrorDetail(response)) ?? mapErrorToUserMessage(response.status),
        isAuthError: false,
        canRetry: userMayRetry(response.status),
      };
      throw error;
    }

    // 204 No Content (and other empty bodies) have nothing to parse.
    if (response.status === 204) {
      return undefined as T;
    }
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  } catch (error) {
    // If it's already an ApiError, decide whether to retry transparently.
    if (isApiError(error)) {
      // Auth errors are never retried — a fresh token is needed, not another attempt.
      // Everything else consults `shouldAutoRetry`, which excludes 502: an upstream
      // outage will not resolve within a 2s backoff, and each reporting attempt costs
      // up to 60s server-side. Those fail fast and surface a Try Again button instead.
      if (error.isAuthError || !shouldAutoRetry(error.status)) {
        throw error;
      }

      if (retryCount < MAX_RETRIES - 1) {
        const delay = BASE_DELAY * Math.pow(2, retryCount);
        await sleep(delay);
        return authenticatedFetch<T>(endpoint, token, options, retryCount + 1);
      }

      throw error;
    }

    // Handle network errors
    if (error instanceof Error) {
      const networkError: ApiError = {
        status: 0,
        message: mapErrorToUserMessage(0, error.message),
        isAuthError: false,
        // Always offer the user a retry on a network failure, even once the internal
        // attempts are exhausted — connectivity is the failure most likely to have
        // recovered by the time they click.
        canRetry: userMayRetry(0),
      };

      // Retry network errors
      if (retryCount < MAX_RETRIES - 1) {
        const delay = BASE_DELAY * Math.pow(2, retryCount);
        await sleep(delay);
        return authenticatedFetch<T>(endpoint, token, options, retryCount + 1);
      }

      throw networkError;
    }

    // Unknown error
    throw {
      status: 0,
      message: 'An unexpected error occurred.',
      isAuthError: false,
      canRetry: false,
    } as ApiError;
  }
}

/**
 * Type guard for ApiError.
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error &&
    'isAuthError' in error
  );
}

/**
 * Hook for making authenticated API requests.
 *
 * Provides loading state, error handling, and automatic retry.
 *
 * @returns Request state and fetch function
 */
export function useAuthenticatedFetch<T>() {
  const token = useAccessToken();
  const [state, setState] = useState<ApiRequestState<T>>({
    loading: false,
    error: null,
    data: null,
    retryCount: 0,
  });

  // Track if component is mounted
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Use a ref for the token so fetchData doesn't need it as a dependency
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const fetchData = useCallback(
    async (endpoint: string, options?: RequestInit) => {
      if (!isMounted.current) return;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        retryCount: 0,
      }));

      try {
        const data = await authenticatedFetch<T>(endpoint, tokenRef.current, options);

        if (!isMounted.current) return;

        setState({
          loading: false,
          error: null,
          data,
          retryCount: 0,
        });

        return data;
      } catch (error) {
        if (!isMounted.current) return;

        const apiError = isApiError(error)
          ? error
          : {
              status: 0,
              message: 'An unexpected error occurred.',
              isAuthError: false,
              canRetry: false,
            };

        setState((prev) => ({
          loading: false,
          error: apiError,
          data: prev.data,
          retryCount: MAX_RETRIES,
        }));

        // Note: We intentionally don't auto-redirect on 401 errors.
        // This prevents redirect loops when the backend auth is misconfigured.
        // The error will be displayed to the user via the error state.

        throw apiError;
      }
    },
    []
  );

  const reset = useCallback(() => {
    setState({
      loading: false,
      error: null,
      data: null,
      retryCount: 0,
    });
  }, []);

  return {
    ...state,
    fetchData,
    reset,
  };
}

/**
 * Hook for fetching data on mount.
 *
 * Automatically fetches data when the component mounts.
 *
 * @param endpoint - API endpoint to fetch
 * @param options - Fetch options
 * @returns Request state and refetch function
 */
export function useApiData<T>(endpoint: string, options?: RequestInit) {
  const { data, loading, error, retryCount, fetchData, reset } =
    useAuthenticatedFetch<T>();

  useEffect(() => {
    // A blank endpoint means "nothing to fetch yet" (e.g. a required filter
    // isn't set) — skip the request rather than hitting an invalid URL.
    if (!endpoint) return;
    fetchData(endpoint, options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const refetch = useCallback(() => {
    fetchData(endpoint, options);
  }, [endpoint, options, fetchData]);

  return {
    data,
    loading,
    error,
    retryCount,
    refetch,
    reset,
  };
}
